import React, { useState } from 'react'
import { useSelector } from 'react-redux'

import { EuiBasicTable, EuiButton, EuiButtonEmpty, EuiButtonIcon, EuiFieldText, EuiFlexGroup, EuiFlexItem, EuiFormRow, EuiIcon, EuiLoadingContent, EuiModal, EuiModalBody, EuiModalFooter, EuiModalHeader, EuiModalHeaderTitle, EuiOverlayMask, EuiSpacer, EuiText } from '@elastic/eui'
import { DefaultItemIconButtonAction } from '@elastic/eui/src/components/basic_table/action_types'
import { EuiBasicTableColumn } from '@elastic/eui/src/components/basic_table/basic_table'

import { PaymentMethod } from 'api/interfaces'
import { useGetBillingProviderQuery } from 'api/rtkQueryApi/platform/coreBillingApi'
import { useAssignDefaultPaymentMethodMutation, useDeletePaymentMethodMutation, useGetPaymentMethodsQuery, useUpdatePaymentMethodNicknameMutation } from 'api/rtkQueryApi/platform/paymentMethodsApi'
import { RootState } from 'app/rootReducer'
import { AddPaymentMethod } from 'components/payments/AddPaymentMethod'
import { formatCurrency } from 'utils/Formatters'

import { useGetOrganizationBillingCustomersQuery } from '../../api/rtkQueryApi/platform/organizationsApi'

const PaymentMethodsDrawer = () => {
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const paymentMethods = useGetPaymentMethodsQuery({ accountId: currentAccount?.id ?? '' }, { skip: !currentAccount?.id })
  const [deletePaymentMethod, deletePaymentMethodRequest] = useDeletePaymentMethodMutation()
  const [assignDefaultPaymentMethod, assignDefaultPaymentMethodRequest] = useAssignDefaultPaymentMethodMutation()
  const [updatePaymentMethodNickname, updatePaymentMethodNicknameRequest] = useUpdatePaymentMethodNicknameMutation()
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)
  const [editingNicknameFor, setEditingNicknameFor] = useState<PaymentMethod | null>(null)
  const [nicknameDraft, setNicknameDraft] = useState<string>('')
  const billingCustomersQuery = useGetOrganizationBillingCustomersQuery(undefined)
  const { isLoading: isLoadingBillingProvider, data: billingProvider } = useGetBillingProviderQuery({ accountId: currentAccount?.id ?? '' }, { skip: !currentAccount?.id })

  const openNicknameEditor = (item: PaymentMethod) => {
    setEditingNicknameFor(item)
    setNicknameDraft(item.nickname ?? '')
  }

  const closeNicknameEditor = () => {
    setEditingNicknameFor(null)
    setNicknameDraft('')
  }

  const saveNickname = async () => {
    if (!currentAccount?.id || !editingNicknameFor) {
      return
    }
    await updatePaymentMethodNickname({
      accountId: currentAccount.id,
      paymentMethodId: editingNicknameFor.id,
      nickname: nicknameDraft.trim()
    })
    closeNicknameEditor()
  }

  const actions: Array<DefaultItemIconButtonAction<PaymentMethod>> = [
    {
      name: 'Remove',
      description: 'Remove this Payment Method',
      icon: 'crossInACircleFilled',
      type: 'icon',
      color: 'danger',
      onClick: (item: PaymentMethod) => {
        if (currentAccount?.id) {
          deletePaymentMethod({ accountId: currentAccount!.id, paymentMethodId: item.id })
        }
      },
      available: (item: PaymentMethod) => !item.isDefault
    },
    {
      name: 'Default',
      description: 'Make this the Default Payment Method',
      icon: 'checkInCircleFilled',
      type: 'icon',
      onClick: (item: PaymentMethod) => {
        assignDefaultPaymentMethod({ accountId: currentAccount!.id, paymentMethodId: item.id })
      },
      available: (item: PaymentMethod) => !item.isDefault
    }
  ]

  const balanceColumns = [
    {
      name: 'Current Balance',
      field: 'currentBalance',
      render: (item: number) => `${formatCurrency(item)}`
    },
    {
      name: 'Pending Charges',
      field: 'pendingCharges',
      render: (item: number) => `${formatCurrency(item)}`
    },
    {
      name: 'Available Balance',
      render: (item: PaymentMethod) => `${formatCurrency(item.availableBalance ?? 0)}`
    }
  ]

  const nonBalanceColumns = [
    {
      name: 'Number',
      field: 'last4',
      render: (item: string) => `ends in ${item}`
    },
    {
      name: 'Expires',
      render: (item: PaymentMethod) => (item.expiryMonth && item.expiryYear ? `${item.expiryMonth}/${item.expiryYear}` : '')
    }
  ]

  const columns: Array<EuiBasicTableColumn<PaymentMethod>> = [
    {
      name: 'Customer',
      render: (item: PaymentMethod) => {
        const customer = billingCustomersQuery.data?.find(customer => customer.id === item.customerId)
        return customer?.name ?? ''
      }
    },
    {
      name: 'Nickname',
      render: (item: PaymentMethod) => (
        <EuiFlexGroup gutterSize='xs' alignItems='center' responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiText size='s'>{item.nickname ? item.nickname : '-'}</EuiText>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButtonIcon aria-label='Edit nickname' iconType='pencil' onClick={() => openNicknameEditor(item)} />
          </EuiFlexItem>
        </EuiFlexGroup>
      )
    },
    {
      name: 'Type',
      field: 'paymentMethodType',
      render: (item: string) => {
        if (item.toUpperCase() === 'CARD') {
          return 'Card'
        } else if (item.toUpperCase() === 'BANKACCOUNT') {
          return 'Bank Account'
        } else if (item.toUpperCase() === 'BALANCE') {
          return 'Balance'
        }
      }
    },
    ...(billingProvider === 'Balance' ? balanceColumns : nonBalanceColumns),
    {
      name: '',
      render: (item: PaymentMethod) =>
        item.isDefault ? (
          <EuiText size={'s'}>
            <EuiIcon type={'check'} /> Default
          </EuiText>
        ) : (
          ''
        )
    },
    {
      name: 'Actions',
      actions
    }
  ]

  const closeModal = () => {
    setIsModalVisible(false)
  }

  const showModal = () => {
    setIsModalVisible(true)
  }

  if (paymentMethods.isLoading || deletePaymentMethodRequest.isLoading || assignDefaultPaymentMethodRequest.isLoading || isLoadingBillingProvider) {
    return <EuiLoadingContent lines={10} />
  }

  return (
    <React.Fragment>
      <EuiBasicTable loading={paymentMethods.isLoading || deletePaymentMethodRequest.isLoading || assignDefaultPaymentMethodRequest.isLoading || updatePaymentMethodNicknameRequest.isLoading} items={paymentMethods.data ?? []} columns={columns} hasActions={true} noItemsMessage={!paymentMethods.isLoading && billingProvider !== 'Balance' && 'please add a payment method'} />
      <EuiSpacer />
      {!isLoadingBillingProvider && billingProvider !== 'Balance' && (
        <EuiButton id='addPayment' onClick={showModal} type='secondary' size='s'>
          Add Payment Method
        </EuiButton>
      )}
      {isModalVisible && currentAccount?.id && (
        <EuiOverlayMask headerZindexLocation='above'>
          <EuiModal onClose={closeModal}>
            <EuiModalHeader>
              <EuiModalHeaderTitle>Add Payment Method</EuiModalHeaderTitle>
            </EuiModalHeader>

            <EuiModalBody>
              <AddPaymentMethod accountId={currentAccount.id} />
            </EuiModalBody>
          </EuiModal>
        </EuiOverlayMask>
      )}
      {editingNicknameFor && (
        <EuiOverlayMask headerZindexLocation='above'>
          <EuiModal onClose={closeNicknameEditor}>
            <EuiModalHeader>
              <EuiModalHeaderTitle>Edit Nickname</EuiModalHeaderTitle>
            </EuiModalHeader>
            <EuiModalBody>
              <EuiFormRow label='Nickname'>
                <EuiFieldText value={nicknameDraft} onChange={e => setNicknameDraft(e.target.value)} autoFocus />
              </EuiFormRow>
            </EuiModalBody>
            <EuiModalFooter>
              <EuiButtonEmpty onClick={closeNicknameEditor}>Cancel</EuiButtonEmpty>
              <EuiButton onClick={saveNickname} fill isLoading={updatePaymentMethodNicknameRequest.isLoading}>
                Save
              </EuiButton>
            </EuiModalFooter>
          </EuiModal>
        </EuiOverlayMask>
      )}
    </React.Fragment>
  )
}

export default PaymentMethodsDrawer
